import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  getData,
  toastNotification,
  updateTenant,
} from "../utils/apiFunctions";
import { ApiEndpoints, LocationPaths } from "../utils/common";
import { useStateContext } from "../contexts/ContextProvider";

const LazyCompanyDetails = lazy(async () => {
  const [moduleExports] = await Promise.all([
    import("../components/CompanyDetails"),
    new Promise((resolve) => setTimeout(resolve, 500)),
  ]);
  return moduleExports;
});
const LazyInventorySettings = lazy(async () => {
  const [moduleExports] = await Promise.all([
    import("../components/InventorySettings"),
    new Promise((resolve) => setTimeout(resolve, 500)),
  ]);
  return moduleExports;
});
const LazyEndpointSettings = lazy(async () => {
  const [moduleExports] = await Promise.all([
    import("../components/EndpointSettings"),
    new Promise((resolve) => setTimeout(resolve, 500)),
  ]);
  return moduleExports;
});
const LazyTenantSettings = lazy(async () => {
  const [moduleExports] = await Promise.all([
    import("../components/TenantSettings"),
    new Promise((resolve) => setTimeout(resolve, 500)),
  ]);
  return moduleExports;
});

let currentSelectedTab = 0;
let hasChanges = false;
let tenantDataToSubmit = null;

let inventorySettingsData = null;
let companyDetailsSettingsData = null;
let endpointSettingsData = null;

const Configuration = () => {
  const [isReadyForRendering, setIsReadyForRendering] = useState(false);
  const [selectedTab, setSelectedTab] = useState(currentSelectedTab);
  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ["fetchTenantData"],
    queryFn: () => getData(`${ApiEndpoints.TENANTS}`),
    notifyOnChangeProps: ["data", "error"],
  });
  const [tenantData, setTenantData] = useState(null);
  const { setLocation } = useStateContext();
  const locationData = useLocation();
  const [companyDetailsSettings, setCompanyDetailsSettings] = useState(null);

  useEffect(() => {
    if (locationData.pathname === LocationPaths.CONFIGURATION) {
      setLocation("Bitdefender / General / Configuration");
    }

    return () => {
      currentSelectedTab = 0;
    };
  }, []);

  useEffect(() => {
    if (!isSuccess) return;

    setTenantData({ ...data });
    inventorySettingsData = (({ inventorySyncSettings, prefixSettings }) => ({
      inventorySyncSettings,
      prefixSettings,
    }))(data);
    companyDetailsSettingsData = (({ gzSettings, gzCompanyDetails }) => ({
      gzSettings,
      gzCompanyDetails,
    }))(data);
    endpointSettingsData = (({
      endpointSyncSettings,
      packageDefaults,
      packageDownloadMethod,
    }) => ({ endpointSyncSettings, packageDefaults, packageDownloadMethod }))(
      data
    );
    setCompanyDetailsSettings({ ...companyDetailsSettingsData });
  }, [isSuccess]);

  let saveButtonRef = useRef(null);
  let gzTabGroupRef = useRef(null);

  const handleCompanyDetailsChange = (gzApiSettingsData) => {
    hasChanges = true;
    setCompanyDetailsSettings({
      ...companyDetailsSettings,
      gzSettings: {
        ...companyDetailsSettings.gzSettings,
        gzApiKey: gzApiSettingsData.gzApiKey,
        gzServerAddress: gzApiSettingsData.gzServerAddress,
      },
    });

    tenantDataToSubmit = {
      ...tenantData,
      gzSettings: {
        ...companyDetailsSettings.gzSettings,
        gzApiKey: gzApiSettingsData.gzApiKey,
        gzServerAddress: gzApiSettingsData.gzServerAddress,
      },
    };
  };

  const handleInventorySettingsChange = (updatedInventorySettingsData) => {
    hasChanges = true;

    setTenantData({
      ...tenantData,
      inventorySyncSettings: updatedInventorySettingsData.inventorySyncSettings,
      prefixSettings: updatedInventorySettingsData.prefixSettings,
    });

    tenantDataToSubmit = {
      ...tenantData,
      inventorySyncSettings: updatedInventorySettingsData.inventorySyncSettings,
      prefixSettings: updatedInventorySettingsData.prefixSettings,
    };

    inventorySettingsData = (({ inventorySyncSettings, prefixSettings }) => ({
      inventorySyncSettings,
      prefixSettings,
    }))(tenantDataToSubmit);
  };

  const handleEndpointSettingsChange = (updatedEndpointSettingsData) => {
    hasChanges = true;

    setTenantData({
      ...tenantData,
      endpointSyncSettings: updatedEndpointSettingsData.endpointSyncSettings,
      packageDefaults: updatedEndpointSettingsData.packageDefaults,
      packageDownloadMethod: updatedEndpointSettingsData.packageDownloadMethod,
    });

    tenantDataToSubmit = {
      ...tenantData,
      endpointSyncSettings: updatedEndpointSettingsData.endpointSyncSettings,
      packageDefaults: updatedEndpointSettingsData.packageDefaults,
      packageDownloadMethod: updatedEndpointSettingsData.packageDownloadMethod,
    };

    endpointSettingsData = (({
      endpointSyncSettings,
      packageDefaults,
      packageDownloadMethod,
    }) => ({ endpointSyncSettings, packageDefaults, packageDownloadMethod }))(
      tenantDataToSubmit
    );
  };

  const saveChanges = async () => {
    saveButtonRef.current.showSpinner = true;
    if (!hasChanges) {
      saveButtonRef.current.showSpinner = false;
      return;
    }

    await updateTenant(tenantDataToSubmit);

    toastNotification("Configuration successfully saved.", "success", false);
    saveButtonRef.current.showSpinner = false;
    hasChanges = false;

    window.location.reload(false);
  };

  useEffect(() => {
    let localSaveButtonRef = null;

    if (isSuccess) {
      gzTabGroupRef.current.addEventListener("gzTabChange", (e) => {
        currentSelectedTab = e.currentTab;
        setSelectedTab(currentSelectedTab);
      });
      localSaveButtonRef = saveButtonRef.current;
      saveButtonRef.current.addEventListener("click", saveChanges);
      setIsReadyForRendering(true);
    }

    return () => {
      if (isSuccess) {
        localSaveButtonRef.removeEventListener("click", saveChanges);
      }
    };
  }, [selectedTab, isSuccess]);

  return (
    <>
      {isLoading && (
        <div className="flex flex-row h-screen items-center justify-center">
          <div className="h-[74px] w-[74px]">
            <gz-progress-spinner spinnerWidth="large" indeterminate />
          </div>
        </div>
      )}
      {isSuccess && (
        <div className="flex flex-grow flex-col h-full">
          <div className="ml-8">
            <gz-title fontSize="20" fontWeight="500" style={{ opacity: "0.7" }}>
              Configuration
            </gz-title>
          </div>
          <div className="flex justify-between items-center w-full mt-4 p-3">
            <gz-tab-group ref={gzTabGroupRef}>
              <gz-tab id="company_details" label="Company info" />
              <gz-tab
                id="inventory_settings"
                label="Inventory Synchronization"
              />
              <gz-tab id="endpoint_settings" label="Agent Synchronization" />
              <gz-tab id="tenant_settings" label="Tenant Settings" />
            </gz-tab-group>
          </div>
          <div className="mt-2 overflow-auto configurationContent">
            {currentSelectedTab === 0 && isReadyForRendering && (
              <Suspense
                fallback={
                  <div className="top-1/2 left-1/2 absolute">
                    <gz-progress-spinner spinnerWidth="large" indeterminate />
                  </div>
                }
              >
                <LazyCompanyDetails
                  companySettings={companyDetailsSettingsData}
                  updateTenantData={handleCompanyDetailsChange}
                />
              </Suspense>
            )}
            {currentSelectedTab === 1 && (
              <Suspense
                fallback={
                  <div className="top-1/2 left-1/2 absolute">
                    <gz-progress-spinner spinnerWidth="large" indeterminate />
                  </div>
                }
              >
                <LazyInventorySettings
                  inventorySettings={inventorySettingsData}
                  updateTenantData={handleInventorySettingsChange}
                />
              </Suspense>
            )}
            {currentSelectedTab === 2 && (
              <Suspense
                fallback={
                  <div className="top-1/2 left-1/2 absolute">
                    <gz-progress-spinner spinnerWidth="large" indeterminate />
                  </div>
                }
              >
                <LazyEndpointSettings
                  agentSettings={endpointSettingsData}
                  updateTenantData={handleEndpointSettingsChange}
                />
              </Suspense>
            )}
            {currentSelectedTab === 3 && (
              <Suspense
                fallback={
                  <div className="top-1/2 left-1/2 absolute">
                    <gz-progress-spinner spinnerWidth="large" indeterminate />
                  </div>
                }
              >
                <LazyTenantSettings
                />
              </Suspense>
            )}
          </div>
          <div className="fixed w-full bottom-0 py-3 ps-12 bg-white shadow-[rgb(0,0,0,0.2)_0px_-6px_6px_-6px]">
            <gz-button
              id="saveConfiguration"
              type="full"
              color="primary"
              ref={saveButtonRef}
              {...(currentSelectedTab === 3) ? { disabled: true } : {}}
            >
              Save
            </gz-button>
          </div>
        </div>
      )}
    </>
  );
};

export default Configuration;
